import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit3, Check, X, Layers, Package, Wrench, ShoppingCart, ChevronRight, ChevronDown, Search, AlertTriangle } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  injected: { label: "Injetado", color: "bg-blue-100 text-blue-700 border-blue-300", icon: <Wrench className="h-3 w-3" /> },
  purchased: { label: "Comprado", color: "bg-amber-100 text-amber-700 border-amber-300", icon: <ShoppingCart className="h-3 w-3" /> },
  assembled: { label: "Montado", color: "bg-emerald-100 text-emerald-700 border-emerald-300", icon: <Layers className="h-3 w-3" /> },
  subassembly: { label: "Subconjunto", color: "bg-indigo-100 text-indigo-700 border-indigo-300", icon: <Package className="h-3 w-3" /> },
};

function TypeBadge({ type }) {
  const t = TYPE_LABELS[type] || { label: type || "?", color: "bg-slate-100 text-slate-600" };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border font-bold ${t.color}`}>
      {t.icon}{t.label}
    </span>
  );
}

export default function BOMEditor() {
  const [parts, setParts] = useState([]);
  const [boms, setBoms] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedBomId, setSelectedBomId] = useState(null);
  const [bomDetail, setBomDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBomDialog, setShowBomDialog] = useState(false);
  const [newBomForm, setNewBomForm] = useState({ version: "V1", notes: "" });
  const [showComponentDialog, setShowComponentDialog] = useState(false);
  const [componentForm, setComponentForm] = useState({ component_part_id: "", quantity: 1, notes: "" });
  const [componentSearch, setComponentSearch] = useState("");
  const [requirementsQty, setRequirementsQty] = useState(100);
  const [requirements, setRequirements] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [search, setSearch] = useState("");

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, bRes] = await Promise.all([
        axios.get(`${API}/parts`),
        axios.get(`${API}/boms`),
      ]);
      setParts(pRes.data || []);
      setBoms(bRes.data || []);
    } catch (e) { showError(e, "Erro a carregar dados"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  const loadBomDetail = async (bomId) => {
    try {
      const r = await axios.get(`${API}/boms/${bomId}`);
      setBomDetail(r.data);
      setSelectedBomId(bomId);
    } catch (e) { showError(e, "Erro a carregar BOM"); }
  };

  const createBom = async () => {
    if (!selectedProductId) { toast.error("Escolha um produto"); return; }
    try {
      const r = await axios.post(`${API}/boms`, {
        product_id: selectedProductId,
        version: newBomForm.version || "V1",
        notes: newBomForm.notes,
        is_active: true,
      });
      toast.success(`BOM ${r.data.version} criado`);
      setShowBomDialog(false);
      setNewBomForm({ version: "V1", notes: "" });
      await loadInitial();
      loadBomDetail(r.data.id);
    } catch (e) { showError(e, "Erro a criar BOM"); }
  };

  const activateBom = async (bomId) => {
    try {
      await axios.post(`${API}/boms/${bomId}/activate`);
      toast.success("BOM activo");
      await loadInitial();
      loadBomDetail(bomId);
    } catch (e) { showError(e, "Erro"); }
  };

  const deleteBom = async (bomId) => {
    if (!window.confirm("Eliminar este BOM? Todos os componentes serão removidos.")) return;
    try {
      await axios.delete(`${API}/boms/${bomId}`);
      toast.success("BOM eliminado");
      setBomDetail(null);
      setSelectedBomId(null);
      await loadInitial();
    } catch (e) { showError(e, "Erro"); }
  };

  const addComponent = async () => {
    if (!componentForm.component_part_id || !componentForm.quantity) { toast.error("Preencha os campos"); return; }
    if (parseFloat(componentForm.quantity) <= 0) { toast.error("Quantidade > 0"); return; }
    try {
      await axios.post(`${API}/bom-items`, {
        bom_id: selectedBomId,
        component_part_id: componentForm.component_part_id,
        quantity: parseFloat(componentForm.quantity),
        notes: componentForm.notes,
      });
      toast.success("Componente adicionado");
      setShowComponentDialog(false);
      setComponentForm({ component_part_id: "", quantity: 1, notes: "" });
      setComponentSearch("");
      loadBomDetail(selectedBomId);
    } catch (e) { showError(e, "Erro"); }
  };

  const updateComponentQty = async (itemId, newQty) => {
    try {
      await axios.put(`${API}/bom-items/${itemId}`, { quantity: parseFloat(newQty) });
      loadBomDetail(selectedBomId);
    } catch (e) { showError(e, "Erro"); }
  };

  const deleteComponent = async (itemId) => {
    if (!window.confirm("Remover este componente?")) return;
    try {
      await axios.delete(`${API}/bom-items/${itemId}`);
      loadBomDetail(selectedBomId);
    } catch (e) { showError(e, "Erro"); }
  };

  const computeRequirements = async () => {
    if (!bomDetail?.product_id) return;
    try {
      const r = await axios.get(`${API}/requirements/${bomDetail.product_id}?quantity=${requirementsQty}`);
      setRequirements(r.data);
    } catch (e) { showError(e, "Erro cálculo"); }
  };

  const bomsFiltered = boms.filter(b => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (b.product_code || "").toLowerCase().includes(s) ||
           (b.product_description || "").toLowerCase().includes(s);
  });

  const partsForComponent = parts
    .filter(p => p.id !== bomDetail?.product_id)
    .filter(p => {
      if (!componentSearch) return true;
      const s = componentSearch.toLowerCase();
      return p.code.toLowerCase().includes(s) || p.description.toLowerCase().includes(s);
    });

  const partsForNewBom = parts.filter(p => ["assembled", "subassembly"].includes(p.product_type || "injected"));

  const toggleNode = (id) => setExpandedNodes(e => ({ ...e, [id]: !e[id] }));

  const renderTreeNode = (node, depth = 0) => {
    if (!node) return null;
    const key = `${node.part_id}-${depth}`;
    const hasChildren = (node.children || []).length > 0;
    const expanded = expandedNodes[key] !== false;
    const shortage = node.shortage_qty > 0;
    return (
      <div key={key} className="border-l-2 border-slate-200" style={{ marginLeft: depth * 16 }}>
        <div className={`flex items-center gap-2 py-1.5 px-2 hover:bg-slate-50 ${shortage ? "bg-rose-50" : ""}`}>
          {hasChildren ? (
            <button onClick={() => toggleNode(key)} className="text-slate-400 hover:text-slate-700">
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <div className="w-4" />
          )}
          <TypeBadge type={node.product_type} />
          <span className="font-mono text-xs text-slate-500">{node.code}</span>
          <span className="text-sm flex-1 truncate">{node.description}</span>
          <span className="text-xs font-semibold text-slate-700">Nec: {node.required_qty}</span>
          <span className={`text-xs font-semibold ${node.stock_qty >= node.required_qty ? "text-emerald-600" : "text-slate-500"}`}>Stock: {node.stock_qty}</span>
          {shortage ? (
            <span className="text-xs font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />Falta {node.shortage_qty}
            </span>
          ) : (
            <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">OK</span>
          )}
        </div>
        {hasChildren && expanded && (
          <div>{node.children.map(c => renderTreeNode(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-5" data-testid="bom-editor-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Layers className="h-7 w-7 text-indigo-600" />Estrutura de Produtos (BOM/MRP)</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gerir componentes de produtos montados. Ao iniciar uma ordem, o sistema reserva automaticamente stock dos componentes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: BOMs list */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" />BOMs Criados ({boms.length})</CardTitle>
              <Button size="sm" onClick={() => setShowBomDialog(true)} data-testid="btn-new-bom">
                <Plus className="h-4 w-4 mr-1" />Novo
              </Button>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar produto..." className="pl-7 h-8 text-xs" />
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (
              <div className="space-y-1 max-h-[600px] overflow-y-auto">
                {bomsFiltered.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-6">Nenhum BOM. Clique em <strong>Novo</strong> para criar.</div>
                )}
                {bomsFiltered.map(b => (
                  <button
                    key={b.id}
                    onClick={() => loadBomDetail(b.id)}
                    className={`w-full text-left p-2 rounded border transition-all ${selectedBomId === b.id ? "bg-indigo-50 border-indigo-500 shadow-sm" : "hover:bg-slate-50 border-transparent"}`}
                    data-testid={`bom-${b.product_code}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold">{b.product_code}</span>
                      <TypeBadge type={b.product_type} />
                      {b.is_active && <span className="text-[9px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">Activo</span>}
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5 truncate">{b.product_description}</div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                      <span>{b.version}</span>
                      <span>•</span>
                      <span>{b.components_count} componentes</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right: BOM Detail */}
        <Card className="lg:col-span-2">
          {!bomDetail ? (
            <CardContent className="pt-16 text-center text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>Selecione um BOM à esquerda ou crie um novo.</p>
            </CardContent>
          ) : (
            <>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="font-mono text-slate-500">{bomDetail.product_code}</span>
                      <span>—</span>
                      <span>{bomDetail.product_description}</span>
                    </CardTitle>
                    <div className="text-xs mt-1 flex items-center gap-2 flex-wrap">
                      <TypeBadge type={bomDetail.product_type} />
                      <span className="text-slate-500">Versão: <strong>{bomDetail.version}</strong></span>
                      {bomDetail.is_active ? (
                        <span className="text-[9px] bg-emerald-500 text-white px-1.5 py-0.5 rounded font-bold uppercase">Activo</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => activateBom(bomDetail.id)}>
                          <Check className="h-3 w-3 mr-1" />Activar
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => setShowComponentDialog(true)} data-testid="btn-add-component">
                      <Plus className="h-4 w-4 mr-1" />Componente
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteBom(bomDetail.id)} className="text-rose-600 hover:bg-rose-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Components table */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Componente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Qtd/Unidade</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(bomDetail.items || []).length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Sem componentes. Clique em <strong>Componente</strong> para adicionar.</TableCell></TableRow>
                    )}
                    {(bomDetail.items || []).map(it => (
                      <TableRow key={it.id}>
                        <TableCell>
                          <div className="font-medium text-sm">{it.component?.code} · {it.component?.description}</div>
                          {it.notes && <div className="text-[10px] text-slate-500">{it.notes}</div>}
                        </TableCell>
                        <TableCell><TypeBadge type={it.component?.product_type} /></TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number" step="0.01" min="0.01"
                            defaultValue={it.quantity}
                            onBlur={(e) => {
                              const v = parseFloat(e.target.value);
                              if (v > 0 && v !== it.quantity) updateComponentQty(it.id, v);
                            }}
                            className="w-20 h-7 text-right ml-auto"
                          />
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">{it.component?.stock_quantity ?? 0}</TableCell>
                        <TableCell>
                          <Button size="icon" variant="ghost" onClick={() => deleteComponent(it.id)} className="h-7 w-7 text-rose-600">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Requirements calculator */}
                <div className="mt-6 border-t pt-4">
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" />Simular Necessidades</h3>
                  <div className="flex items-end gap-2 flex-wrap">
                    <div>
                      <Label className="text-xs">Quantidade a produzir</Label>
                      <Input type="number" min="1" value={requirementsQty} onChange={e => setRequirementsQty(e.target.value)} className="w-32 h-9" />
                    </div>
                    <Button onClick={computeRequirements} data-testid="btn-compute-req">Calcular</Button>
                    {requirements && (
                      <div className="text-xs ml-auto">
                        {requirements.can_produce ? (
                          <span className="bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded font-bold">✓ Pode produzir - stock suficiente</span>
                        ) : (
                          <span className="bg-rose-100 text-rose-800 px-3 py-1.5 rounded font-bold">✗ Faltam {requirements.components_short_count} componentes</span>
                        )}
                      </div>
                    )}
                  </div>
                  {requirements && (
                    <div className="mt-3 border rounded-lg bg-white overflow-hidden">
                      <div className="bg-slate-100 px-3 py-2 text-xs font-bold uppercase text-slate-600">Explosão (multi-nível)</div>
                      <div className="max-h-[400px] overflow-y-auto p-2">
                        {renderTreeNode(requirements.tree, 0)}
                      </div>
                      {requirements.flat_components?.length > 0 && (
                        <>
                          <div className="bg-slate-100 px-3 py-2 text-xs font-bold uppercase text-slate-600 mt-1">Agregado (necessário total)</div>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Componente</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead className="text-right">Necessário</TableHead>
                                <TableHead className="text-right">Stock</TableHead>
                                <TableHead className="text-right">Falta</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {requirements.flat_components.map(f => (
                                <TableRow key={f.part_id} className={f.shortage_qty > 0 ? "bg-rose-50" : ""}>
                                  <TableCell><div className="font-mono text-xs">{f.code}</div><div className="text-xs text-slate-500">{f.description}</div></TableCell>
                                  <TableCell><TypeBadge type={f.product_type} /></TableCell>
                                  <TableCell className="text-right font-bold">{f.required_qty}</TableCell>
                                  <TableCell className="text-right">{f.stock_qty}</TableCell>
                                  <TableCell className={`text-right font-bold ${f.shortage_qty > 0 ? "text-rose-700" : "text-emerald-700"}`}>{f.shortage_qty > 0 ? `-${f.shortage_qty}` : "0"}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* New BOM Dialog */}
      <Dialog open={showBomDialog} onOpenChange={setShowBomDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo BOM</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Produto final</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={selectedProductId}
                onChange={e => setSelectedProductId(e.target.value)}
                data-testid="new-bom-product"
              >
                <option value="">Selecione um produto...</option>
                {partsForNewBom.map(p => (
                  <option key={p.id} value={p.id}>{p.code} - {p.description} [{TYPE_LABELS[p.product_type]?.label}]</option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Só aparecem produtos com tipo "Montado" ou "Subconjunto". Edite a peça primeiro para mudar o tipo.</p>
            </div>
            <div>
              <Label>Versão</Label>
              <Input value={newBomForm.version} onChange={e => setNewBomForm({...newBomForm, version: e.target.value})} placeholder="V1" />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={newBomForm.notes} onChange={e => setNewBomForm({...newBomForm, notes: e.target.value})} placeholder="Opcional" />
            </div>
            <Button onClick={createBom} className="w-full" data-testid="btn-create-bom">Criar BOM</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Component Dialog */}
      <Dialog open={showComponentDialog} onOpenChange={setShowComponentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Adicionar Componente</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Pesquisar componente</Label>
              <Input value={componentSearch} onChange={e => setComponentSearch(e.target.value)} placeholder="Código ou descrição..." />
            </div>
            <div className="max-h-64 overflow-y-auto border rounded">
              {partsForComponent.slice(0, 100).map(p => (
                <button
                  key={p.id}
                  onClick={() => setComponentForm({...componentForm, component_part_id: p.id})}
                  className={`w-full text-left p-2 border-b hover:bg-indigo-50 flex items-center gap-2 ${componentForm.component_part_id === p.id ? "bg-indigo-100" : ""}`}
                  data-testid={`comp-opt-${p.code}`}
                >
                  <TypeBadge type={p.product_type} />
                  <span className="font-mono text-xs">{p.code}</span>
                  <span className="text-sm flex-1 truncate">{p.description}</span>
                  <span className="text-xs text-slate-500">stock: {p.stock_quantity || 0}</span>
                </button>
              ))}
              {partsForComponent.length === 0 && <div className="p-4 text-center text-muted-foreground text-sm">Nenhuma peça encontrada</div>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Quantidade por unidade *</Label>
                <Input type="number" step="0.01" min="0.01" value={componentForm.quantity} onChange={e => setComponentForm({...componentForm, quantity: e.target.value})} data-testid="comp-qty" />
              </div>
              <div>
                <Label>Notas</Label>
                <Input value={componentForm.notes} onChange={e => setComponentForm({...componentForm, notes: e.target.value})} placeholder="Opcional" />
              </div>
            </div>
            <Button onClick={addComponent} disabled={!componentForm.component_part_id} className="w-full" data-testid="btn-save-component">
              Adicionar Componente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
